import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { FiatDepositStateService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit.state.service";
import { FiatDepositStepsData } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-data.model";
import { FiatDepositNavigationService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit-navigation.service";
import { FiatDepositStepsParams } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-params.model";
import { PaymentOptionsFacade } from "@modules/+new/payments/features/payment-options/domain/+state/payment-options.facade";
import { PaymentOptionsForFiatSummary } from "@modules/+new/payments/features/payment-options/domain/entities/payment-options-summary.model";
import { FiatDepositValidationService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit-validation.service";
import { FiatDepositStepNames } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-names.enum";
import { FiatDepositStepValidationStatus } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-step-validation-status.model";
import { FiatDepositRemote } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit.remote";
import { DryOffer } from "@modules/+new/payments/features/fiat-deposit/domain/entities/dry-offer/dry-offer.model";
import { WalletsFacade } from "@modules/+new/wallets/domain/+state/wallets.facade";
import { FiatWalletSummary } from "@modules/+new/wallets/domain/entities/fiat-wallet-summary.model";
import { PaymentOption } from "@modules/+new/payments/features/payment-options/domain/entities/payment-option.model";
import { Limits } from "@modules/+new/payments/shared-payments/entities/limits.model";
import { AssetsFacade } from "@modules/+new/assets/domain/+state/assets.facade";
import { Fiat } from "@modules/+new/assets/domain/entities/fiat.model";
import { CryptoCoinWalletSummary } from "@modules/+new/wallets/domain/entities/cryptocoin-wallet-summary.model";
import { Bank } from "@modules/+new/payments/features/fiat-deposit/domain/entities/bank/bank.model";
import { take, map, tap } from "rxjs/operators";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { ActivatedRoute } from "@angular/router";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { GetBankTransferInformationRequest } from "@modules/+new/payments/features/fiat-deposit/domain/entities/manual-deposit/get-deposit-information-request.model";
import { FiatDepositBridgeService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit-bridge.service";
import { BankTransferInformation } from "@modules/+new/payments/features/fiat-deposit/domain/entities/manual-deposit/bank-transfer-information.model";
import { CreateDepositTransactionRequest } from "@modules/+new/transactions/domain/entities/create-deposit-transaction-request.model";
import { FiatTransaction } from "@modules/+new/transactions/domain/entities/fiat-transaction.model";
import { TransactionsFacade } from "@modules/+new/transactions/domain/+state/transactions.facade";
import { FiatDepositDryOfferService } from "@modules/+new/payments/features/fiat-deposit/domain/+state/fiat-deposit-dry-offer.service";
import { ConfirmDepositTransactionRequest } from "@modules/+new/transactions/domain/entities/confirm-deposit-transaction-request.model";
import { ApproveDepositTransactionRequest } from "@modules/+new/transactions/domain/entities/approve-deposit-transaction-request.model";
import { FinalizationTypes } from "@modules/+new/payments/features/fiat-deposit/domain/entities/finalization-types.enum";
import { CheckoutDepositData } from "@modules/checkout/entities/checkout.deposit-data";

@Injectable({
	providedIn: "any"
})
export class FiatDepositFacade {
	public fromSummary$: Observable<{ value: boolean }> = this.route.queryParams.pipe(
		map(({ fromSummary }) => ({ value: Boolean(fromSummary) }))
	);

	public constructor(
		private readonly fiatDepositNavigationService: FiatDepositNavigationService,
		private readonly fiatDepositValidationService: FiatDepositValidationService,
		private readonly fiatDepositStateService: FiatDepositStateService,
		private readonly paymentOptionsFacade: PaymentOptionsFacade,
		private readonly fiatDepositRemote: FiatDepositRemote,
		private readonly fiatDepositBridgeService: FiatDepositBridgeService,
		private readonly assetsFacade: AssetsFacade,
		private readonly walletsFacade: WalletsFacade,
		private readonly modal: UniversalModalService,
		private readonly route: ActivatedRoute,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly transactionsFacade: TransactionsFacade,
		private readonly fiatDepositDryOfferService: FiatDepositDryOfferService
	) {}

	public cancelModal = ($event?: MouseEvent): void => {
		$event?.preventDefault();
		const reason = window !== window.parent ? ModalCloseReason.close : ModalCloseReason.cancel;
		this.modal.close(reason);
	};

	public closeModal = ($event?: MouseEvent): void => {
		$event?.preventDefault();
		this.modal.close(ModalCloseReason.close);
	};

	public getPaymentOptionsSummaryByFiat$(fiatId: number): Observable<PaymentOptionsForFiatSummary> {
		return this.paymentOptionsFacade.getPaymentOptionsSummaryByFiat$(fiatId);
	}

	public getPaymentOptionById$(id: number): Observable<PaymentOption> {
		return this.paymentOptionsFacade.getPaymentOptionById$(id);
	}

	public getPaymentOptionLimits$(paymentOptionId: number, fiatId: number): Observable<Limits> {
		return this.paymentOptionsFacade.getLimits$(paymentOptionId, fiatId);
	}

	public getStepsData$(): Observable<FiatDepositStepsData> {
		return this.fiatDepositStateService.getStepsData$();
	}

	public setStepsData(data: FiatDepositStepsData): void {
		return this.fiatDepositStateService.setStepsData(data);
	}

	public navigateToStep$(stepName: FiatDepositStepNames, params?: FiatDepositStepsParams): Observable<boolean> {
		return this.fiatDepositNavigationService.navigateToStep$(stepName, params);
	}

	public navigateToStep(stepName: FiatDepositStepNames, params?: FiatDepositStepsParams): void {
		this.navigateToStep$(stepName, params).pipe(take(1)).subscribe();
	}

	public backToStep = (stepName: FiatDepositStepNames, $event?: MouseEvent): void => {
		$event?.preventDefault();
		return this.navigateToStep(stepName, { fromBack: true });
	};

	public toSummary = ($event: MouseEvent): void => {
		$event.preventDefault();
		return this.navigateToStep(FiatDepositStepNames.Summary);
	};

	public isStateValid$(): Observable<FiatDepositStepValidationStatus> {
		return this.fiatDepositValidationService.isStateValid$();
	}

	public isStepStateValid$(
		stepName: FiatDepositStepNames,
		stepsData: FiatDepositStepsData,
		fromBack?: boolean,
		fromCheckout?: boolean
	): Observable<FiatDepositStepValidationStatus> {
		return this.fiatDepositValidationService.isStepStateValid$(stepName, stepsData, fromBack, fromCheckout);
	}

	public getFiats$(): Observable<Fiat[]> {
		return this.assetsFacade.getFiats$();
	}

	public getDefaultFiat$(): Observable<Fiat> {
		return this.assetsFacade.getDefaultFiat$();
	}

	public getFiatWalletsSummary$(): Observable<FiatWalletSummary[]> {
		return this.walletsFacade.getFiatWalletsSummary$();
	}

	public getFiatWalletsSummaryByWalletGuid$(fiatWalletGuid: string): Observable<FiatWalletSummary> {
		return this.walletsFacade.getFiatWalletsSummaryByWalletGuid$(fiatWalletGuid);
	}

	public getCryptoCoinWalletsSummary$(): Observable<CryptoCoinWalletSummary[]> {
		return this.walletsFacade.getCryptoCoinWalletsSummary$();
	}

	public getBank$(bankProviderName: string, bankId: string): Observable<Bank> {
		return this.fiatDepositRemote.getBank$(bankProviderName, bankId);
	}

	public getBanks$(bankProviderName: string, query?: string, page?: number): Observable<Bank[]> {
		return this.fiatDepositRemote.getBanks$(bankProviderName, query, page);
	}

	public getBankChildList$(
		bankProviderName: string,
		parentBankId: string,
		query?: string,
		page?: number
	): Observable<Bank[]> {
		return this.fiatDepositRemote.getBankChildList$(bankProviderName, parentBankId, query, page);
	}

	public getBankTransferInformation$(
		request: GetBankTransferInformationRequest
	): Observable<BankTransferInformation> {
		const userId = this.fiatDepositBridgeService.getUserId();

		return this.fiatDepositRemote
			.getBankTransferInformation$(userId, request)
			.pipe(
				tap(() =>
					this.infoService.setSuccess(this.translationService.translate("deposit_email_successful_send"))
				)
			);
	}

	public getLastUsedBanks$(bankProviderName: string): Observable<Bank[]> {
		return this.fiatDepositRemote.getLastUsedBanks$(bankProviderName);
	}

	public getDryOfferByStepsData$(stepsData: FiatDepositStepsData): Observable<DryOffer> {
		return this.fiatDepositDryOfferService.getDryOfferByStepsData$(stepsData);
	}

	public createDepositTransaction$(request: CreateDepositTransactionRequest): Observable<FiatTransaction> {
		return this.transactionsFacade.createDepositTransaction$(request);
	}

	public confirmDepositTransaction$(request: ConfirmDepositTransactionRequest): Observable<FiatTransaction> {
		return this.transactionsFacade.confirmDepositTransaction$(request);
	}

	public approveDepositTransaction$(request: ApproveDepositTransactionRequest): Observable<void> {
		return this.transactionsFacade.approveDepositTransaction$(request);
	}

	// TODO: this func opens old modal with paysafe modal flow
	// temp solution, will be solved "correctly" while refactoring paysafe flow (old -> new modals)
	public openOldPaysafeModal(
		walletGuid: string,
		transactionId: string,
		paymentLink: string,
		isCheckout?: boolean,
		checkoutDepositData?: CheckoutDepositData
	): void {
		this.closeModal();
		this.fiatDepositBridgeService.openOldPaysafeModal(
			walletGuid,
			transactionId,
			paymentLink,
			isCheckout,
			checkoutDepositData
		);
	}

	public isPaysafe(finalizationType: FinalizationTypes, redirectUrl: string): boolean {
		return this.fiatDepositBridgeService.isPaysafe(finalizationType, redirectUrl);
	}
}
